import React, { useEffect, useMemo, useState } from 'react'
import SlideBase from './SlideBase'
import WrappedVideoBackground from '../WrappedVideoBackground'
import WrappedYearAnimation from '../WrappedYearAnimation'
import FadeBackground from '../FadeBackground'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import Caption from 'LuxKit/Typography/Caption'
import { useWrappedStorySelector } from 'contexts/Wrapped'
import { WRAPPED_SLIDES } from '../../config/slides'
import { useAppSelector } from 'hooks/reduxHooks'

// Get video config for this slide (static, doesn't depend on runtime data)
const SLIDE_VIDEO = WRAPPED_SLIDES.find(s => s.id === 'intro')?.video
const SLIDE_ID = 'intro' as const

function IntroSlide() {
  // Use granular selectors to minimize re-renders
  const data = useWrappedStorySelector(state => state.data)
  const totalsData = useWrappedStorySelector(state => state.totalsData)
  const isVideoReady = useWrappedStorySelector(state => state.isVideoReady)
  const isCurrentSlide = useWrappedStorySelector(state => state.currentSlide?.id === SLIDE_ID)

  const [animate, setAnimate] = useState(!SLIDE_VIDEO) // If no video, animate immediately

  // Totals-only mode is determined by not having user wrapped data
  const isTotalsOnlyMode = !data

  const firstName = useAppSelector(state => state.auth.account.givenName)
  const intro = firstName ? `${firstName}, ` : ''

  // Start animation when video is ready (only when this is the current slide)
  useEffect(() => {
    if (SLIDE_VIDEO && isVideoReady && isCurrentSlide) {
      setAnimate(true)
    }
  }, [isVideoReady, isCurrentSlide])

  // Memoize the video background to prevent recreation
  const videoBackground = useMemo(() => {
    if (!SLIDE_VIDEO) return undefined
    return <WrappedVideoBackground video={SLIDE_VIDEO} isActive={isCurrentSlide} />
  }, [isCurrentSlide])

  // Need either user data or totals data to render
  if (!data && !totalsData) return null

  const hasVisited = !!data?.stats.citiesVisited.length

  const heading = isTotalsOnlyMode
    ? "let's look back at a year of travel with Luxury Escapes!"
    : hasVisited ? "let's look back at your escapes!" : "let's look back at a year of travel with Luxury Escapes!"
  return (
    <SlideBase background={videoBackground}>
      {animate &&
        <FadeBackground>
          <Group direction="vertical" verticalAlign="end" horizontalAlign="start" gap={12}>
            <Group direction="vertical" gap={4}>
              <Caption colour="highlight-primary" variant="small" format="uppercase">{isTotalsOnlyMode ? 'A' : 'Your'} Year With Luxury Escapes</Caption>
              <Heading variant="heading2" format="propercase">
                {intro}{heading}
              </Heading>
            </Group>
            <WrappedYearAnimation />
          </Group>
        </FadeBackground>}
    </SlideBase>
  )
}

export default React.memo(IntroSlide)
